import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/lib/auditLog';

export function useProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['profile', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useUpdateProfile() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: {
      first_name?: string;
      last_name?: string;
      phone?: string;
      state_of_residence?: string;
      organization_name?: string;
      avatar_url?: string;
      professional_bio?: string | null;
      years_experience?: number | null;
      specialties?: string[];
      service_states?: string[];
      notification_preferences?: Record<string, boolean>;
      browser_push_enabled?: boolean;
      role?: 'client' | 'agent' | 'lawyer';
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      await supabase.auth.updateUser({
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
          phone: data.phone,
          role: data.role,
        },
      });
      await logActivity('kyc_updated', {
        status: data.nin_verified ? 'verified' : 'pending',
      }, data.id, 'kyc');
      await refreshProfile();
      queryClient.setQueryData(['profile', user?.id], data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useSaveKycDraft() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (draftData: {
      first_name?: string;
      last_name?: string;
      phone?: string | null;
      address?: string | null;
      state_of_residence?: string | null;
      date_of_birth?: string | null;
      gender?: 'male' | 'female' | null;
      nin?: string | null;
      professional_bio?: string | null;
      years_experience?: number | null;
      specialties?: string[];
      service_states?: string[];
      role?: 'client' | 'agent' | 'lawyer';
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase.rpc('submit_kyc_draft', {
        p_first_name: draftData.first_name || '',
        p_last_name: draftData.last_name || '',
        p_phone: draftData.phone || null,
        p_role: draftData.role || 'client',
        p_date_of_birth: draftData.date_of_birth || null,
        p_gender: draftData.gender || null,
        p_address: draftData.address || null,
        p_state_of_residence: draftData.state_of_residence || null,
        p_nin_last_four: draftData.nin?.slice(-4) || null,
        p_professional_bio: draftData.professional_bio || null,
        p_years_experience: draftData.years_experience || null,
        p_specialties: draftData.specialties || [],
        p_service_states: draftData.service_states || [],
      });

      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(['profile', user?.id], data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      await refreshProfile();
    },
  });
}

export function useUpdateKYC() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (kycData: {
      first_name: string;
      last_name: string;
      phone: string;
      address: string;
      state_of_residence: string;
      date_of_birth: string;
      gender: 'male' | 'female';
      nin: string;
      professional_bio?: string | null;
      years_experience?: number | null;
      specialties?: string[];
      service_states?: string[];
      is_verified: boolean;
      nin_verified: boolean;
      kyc_status: 'incomplete' | 'pending_review' | 'approved' | 'rejected';
      kyc_submitted_at?: string | null;
      kyc_rejection_reason?: string | null;
      role?: 'client' | 'agent' | 'lawyer';
      google_role_pending?: boolean;
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase.rpc('submit_kyc_for_review', {
        p_first_name: kycData.first_name,
        p_last_name: kycData.last_name,
        p_phone: kycData.phone,
        p_role: kycData.role || 'client',
        p_date_of_birth: kycData.date_of_birth,
        p_gender: kycData.gender,
        p_address: kycData.address,
        p_state_of_residence: kycData.state_of_residence,
        p_nin_last_four: kycData.nin.slice(-4),
        p_professional_bio: kycData.professional_bio || null,
        p_years_experience: kycData.years_experience || null,
        p_specialties: kycData.specialties || [],
        p_service_states: kycData.service_states || [],
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      await logActivity('kyc_updated', {
        status: data.kyc_status || 'pending_review',
      }, data.id, 'kyc');
      await refreshProfile();
      queryClient.setQueryData(['profile', user?.id], data);
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
